import { Button, type Job, Modal, TraitementLong } from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import useModalSuperUserValidation from '../../../../../hooks/useModalSuperUserValidation';
import type BordereauxRejetCollectivite from '../../../../../interfaces/pieces/bordereaux/BordereauxRejetCollectivite';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import { rejeterBordereau } from '../../../../../services/piecesComptables/bordereaux/bordereaux';

interface BordereauRejeterCollectiviteModalProps {
	onValidate: (data: BordereauxRejetCollectivite) => void;
	onClose: VoidFunction;
	bordereau: BordereauWithState;
	visible: boolean;
	isSuperUser: boolean;
}

const BordereauRejeterCollectiviteModal = ({
	onValidate,
	onClose,
	bordereau,
	visible,
	isSuperUser,
}: BordereauRejeterCollectiviteModalProps) => {
	const [job, setJob] = React.useState<Job | null>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const { isValidated, validationComponent } = useModalSuperUserValidation();

	const handleJobError = () => {
		setJob(null);
		setIsLoading(false);
		toast.error('Erreur lors du rejet du bordereau par la collectivité.');
	};

	const handleCloseJob = (finishedJob: Job) => {
		setJob(null);

		if (finishedJob.status === 1) {
			try {
				const response: { original: BordereauxRejetCollectivite } = JSON.parse(
					finishedJob.resultat,
				);
				onValidate({
					nouveauBordereau: {
						...response.original.nouveauBordereau,
						montant: bordereau.montant,
					},
					ancienBordereau: {
						...response.original.ancienBordereau,
						montant: bordereau.montant,
					},
				});
			} catch (error) {
				toast.error(
					getErrorMessage(
						error,
						'Une erreur est survenue lors du rejet de bordereau.',
					),
				);
			} finally {
				setIsLoading(false);
				onClose();
			}
		} else {
			toast.error(finishedJob.resultat);
			setIsLoading(false);
		}
	};

	const handleValidate = async (): Promise<void> => {
		setIsLoading(true);
		const onRejetJob = await rejeterBordereau(bordereau.identifiant as number);

		setJob(onRejetJob);
	};

	return (
		<Modal
			open={visible}
			onClose={onClose}
			closeIcon
			data-testid="bordereauRejeterCollectiviteModal"
		>
			<Modal.Header>
				Rejeter le bordereau à l’initiative de la collectivité
			</Modal.Header>
			<Modal.Content>
				<p>
					Cette action abandonne le traitement comptable de ce bordereau : il ne
					sera connu du comptable public et fera l’objet en interne de la
					création d’un bordereau de rejet pour assurer la traçabilité de
					l’opération.
				</p>
				{validationComponent}
				{isLoading && job && (
					<TraitementLong
						title="Rejet du bordereau par la collectivité"
						identifiantJob={job.identifiant}
						onFinish={handleCloseJob}
						onError={handleJobError}
					/>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose} data-testid="rejeterCollectiviteModalCancel">
					Annuler
				</Button>
				<Button
					disabled={isSuperUser && !isValidated}
					primary
					onClick={handleValidate}
					data-testid="rejeterCollectiviteModalConfirm"
				>
					Confirmer
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default BordereauRejeterCollectiviteModal;
